import React, { ReactElement, ReactNode } from 'react'
import { Field } from 'formik'
import { MissionFormValues } from '../../missions/mission-form'

import {
  Clipboard as ClipboardType,
  ClipboardProvider,
  useClipboardContext,
} from '../../../hooks/use-clipboard'
import { UseCalendar, CalendarProvider, IsoTime, IsoDate } from '../../../hooks/use-calendar'
import { calculateWorkPeriodDuration } from '../../../utils/work-periods'
import { Day } from '../../schedule/day'
import { Week } from '../../schedule/week'
import { WeeksList } from '../../schedule/weeks-list'
import { Columns, Element } from 'react-bulma-components'
import { dateToTimeInputValue, formatISODate } from '../../../utils/date'
import { PasteButton } from '../../clipboard/paste-button'
import { CopyButton } from '../../clipboard/copy-button'
import { DayCellActions } from '../../schedule/day-cell-actions'
import {
  getExistingWorkPeriod,
  getWorkPeriodsAfterPasteDay,
  getWorkPeriodsAfterPasteToWeekday,
  getWorkPeriodsAfterPasteWeek,
  getWeekCopyPayload,
  getDayCopyPayload,
  removeWorkPeriod,
} from './utils'
import { WorkPeriodEditor, WorkPeriodEditorFormErrors } from './work-period-editor'
import InputField from '../../form/fields/input'
import { WorkPeriodsDuration } from '../work-periods-duration'
import { Mission } from '../../../api/missions'
import { isBefore } from 'date-fns'
import { FormFieldErrorMessage } from '../../form/fields/form-field'
import { WorkPeriod } from '../../../api/work-periods'
import { WorkersRequest } from '../../../api/workers-requests'
import { useJobTitlesQuery } from '../../../queries/job-titles'

interface WorkPeriodsEditorMeta {
  meta: {
    startDate: string // YYYY-mm-dd
    endDate: string // YYYY-mm-dd
    resource?: Mission | WorkersRequest
  }
}

type WorkPeriodsEditorWorkPeriodsValue = Array<
  {
    start?: { date: Date }
    end?: { date: Date }
  } & Partial<Omit<WorkPeriod, 'start' | 'end'>>
>

type CalendarData = MissionFormValues['workPeriods'][0]
type CalendarMetaData = { duration: number; isDisabled: boolean }

/**
 * New Mission
 * FormState -> Internal State -> Form Input \
 *    ^---------------------- onChange ------|
 *
 * Update Mission
 * Mission -> FormState -> Internal State -> Form Input \
 *    ^                          ^----- onChange -------|
 *    |------------------------------------- Save ------|
 */

export const isResourceDisabled = <Resource extends Mission | WorkersRequest>(
  resource?: Resource,
): boolean => {
  if (!resource) return false
  if (resource.__actions.canBeUpdated) return false
  return true
}

export const isWorkPeriodDisabled = <Resource extends Mission | WorkersRequest>(
  isoDate: IsoDate | null,
  resource?: Resource,
  workPeriod?: WorkPeriodsEditorWorkPeriodsValue[number],
): boolean => {
  if (isoDate === null) return true // out of range cell
  // if no mission we are in a create state,
  // Nothing is disabled
  if (!resource) return false
  // Here starts update state
  if (workPeriod) {
    if (workPeriod.__actions?.canBeUpdated === false) return true
  }

  return false
}

export const WorkPeriodsEditor = ({
  workPeriods,
  jobTitle,
  initialWorkPeriods,
  resource,
  user,
  setWorkPeriods,
  errors,
  startDate,
  endDate,
  /** When true, hides the mission start/end date inputs (e.g. parent controls range via a week filter). */
  hideDateRange = false,
}: {
  workPeriods: WorkPeriodsEditorWorkPeriodsValue
  jobTitle?: string
  initialWorkPeriods: WorkPeriodsEditorWorkPeriodsValue
  resource?: Mission | WorkersRequest
  user?: string
  setWorkPeriods: (wPs: WorkPeriodsEditorWorkPeriodsValue) => void
  errors: WorkPeriodEditorFormErrors
  startDate: WorkPeriodsEditorMeta['meta']['startDate']
  endDate: WorkPeriodsEditorMeta['meta']['endDate']
  hideDateRange?: boolean
}): ReactNode => {
  const jobTitlesQuery = useJobTitlesQuery()
  const currentClockingRule = jobTitle
    ? jobTitlesQuery.data?.find(jT => jobTitle.toString() === jT._id.toString())?.clockingRule
    : undefined

  const dateRangeError = Boolean(errors.meta?.startDate) || Boolean(errors.meta?.endDate)
  /** Parent supplies range elsewhere; do not gate the calendar on hidden meta field errors. */
  const showWeeksList = hideDateRange ? true : !dateRangeError
  const minEndDate = resource ? formatISODate(new Date()) : undefined

  return (
    <CalendarProvider<CalendarData, CalendarMetaData>
      startDateISO={startDate}
      endDateISO={endDate}
      select={{
        day: (wPs, isoDate: string) => getExistingWorkPeriod(wPs, isoDate) ?? null,
      }}
      metadata={{
        day: (wP, isoDate) => ({
          duration: calculateWorkPeriodDuration(wP?.start?.date, wP?.end?.date),
          isDisabled: isWorkPeriodDisabled(isoDate as IsoDate, resource, wP),
        }),
      }}
      data={workPeriods}
    >
      <ClipboardProvider>
        <Clipboard<ClipboardValues>>
          {clipboard => (
            <UseCalendar<CalendarData, CalendarMetaData>>
              {({ calendar, getDay, getWeekDays }) => {
                return (
                  <>
                    {!hideDateRange && (
                      <Columns>
                        <Columns.Column>
                          <Field
                            label="Date de début"
                            name="meta.startDate"
                            type="date"
                            component={InputField}
                            dataTest={`start-date-input-${startDate}`}
                            required
                          />
                          <FormFieldErrorMessage error={errors.meta?.startDate} />
                        </Columns.Column>
                        <Columns.Column>
                          <Field
                            label="Date de fin"
                            name="meta.endDate"
                            type="date"
                            component={InputField}
                            dataTest={`end-date-input-${endDate}`}
                            min={minEndDate}
                            required
                          />
                          <FormFieldErrorMessage error={errors.meta?.endDate} />
                        </Columns.Column>
                      </Columns>
                    )}
                    {showWeeksList && (
                      <WeeksList>
                        {errors.workPeriods && (
                          <tr>
                            <td colSpan={8}>
                              <FormFieldErrorMessage
                                error={errors.workPeriods as string | string[]}
                              />
                            </td>
                          </tr>
                        )}
                        <WeeksList.Header
                          right={isoWeekDay => (
                            <PasteButton
                              size="extra-small"
                              data-test={`weekday-paste-action-${isoWeekDay}`}
                              disabled={
                                clipboard.isEmpty ||
                                isResourceDisabled(resource) ||
                                !isClipboardDay(clipboard.value)
                              }
                              onClick={() => {
                                if (isoWeekDay === null) return
                                if (clipboard.isEmpty || !isClipboardDay(clipboard.value)) return
                                const [start, end] = clipboard.value ?? []
                                if (start === undefined && end === undefined) return
                                const days = getWeekDays(isoWeekDay).filter(
                                  d => !d.metadata?.isDisabled,
                                )
                                setWorkPeriods(
                                  getWorkPeriodsAfterPasteToWeekday(workPeriods, days, [
                                    start,
                                    end,
                                  ]),
                                )
                              }}
                            />
                          )}
                        />
                        {Object.entries(calendar).map(([yearNumber, weeks]) =>
                          Object.entries(weeks).map(([weekNumber, week]) => {
                            return (
                              <Week
                                week={parseInt(weekNumber)}
                                weekYear={parseInt(yearNumber)}
                                calendarWeek={week}
                                startDate={startDate}
                                key={yearNumber + '' + weekNumber}
                              >
                                <Week.Header
                                  week={parseInt(weekNumber)}
                                  weekYear={parseInt(yearNumber)}
                                  right={
                                    <>
                                      <CopyButton
                                        size="extra-small"
                                        disabled={isResourceDisabled(resource)}
                                        data-test={`week-copy-action`}
                                        onClick={() => {
                                          clipboard.copy(
                                            getWeekCopyPayload(week) as ClipboardValues,
                                          )
                                        }}
                                      />
                                      <PasteButton
                                        size="extra-small"
                                        data-test={`week-paste-action`}
                                        disabled={
                                          clipboard.isEmpty ||
                                          isResourceDisabled(resource) ||
                                          isClipboardDay(clipboard.value) ||
                                          week.some(d => d?.metadata?.isDisabled)
                                        }
                                        onClick={() => {
                                          if (
                                            clipboard.isEmpty ||
                                            !clipboard.value ||
                                            isClipboardDay(clipboard.value)
                                          )
                                            return
                                          setWorkPeriods(
                                            getWorkPeriodsAfterPasteWeek(
                                              workPeriods,
                                              week,
                                              clipboard.value,
                                            ),
                                          )
                                        }}
                                      />
                                    </>
                                  }
                                >
                                  <Element textSize={7} mt={1}>
                                    <WorkPeriodsDuration
                                      workPeriods={workPeriods}
                                      breakRule={currentClockingRule?.break}
                                      userId={user}
                                      period={`${parseInt(yearNumber)}-W${parseInt(weekNumber)}`}
                                      year={parseInt(yearNumber)}
                                      week={parseInt(weekNumber)}
                                    />
                                  </Element>
                                </Week.Header>
                                {week.map(day => {
                                  return (
                                    <Day
                                      label={day?.isoDate ? getDay(day.isoDate)?.day : undefined}
                                      empty={!day?.isoDate}
                                      right={
                                        <DayCellActions
                                          onCopy={() => {
                                            const payload = day ? getDayCopyPayload(day) : null
                                            if (payload) clipboard.copy(payload)
                                          }}
                                          onPaste={() => {
                                            if (
                                              !clipboard.value ||
                                              !day?.isoDate ||
                                              !isClipboardDay(clipboard.value)
                                            )
                                              return
                                            setWorkPeriods(
                                              getWorkPeriodsAfterPasteDay(
                                                workPeriods,
                                                day.isoDate,
                                                clipboard.value,
                                              ),
                                            )
                                          }}
                                          onRemove={() => {
                                            if (!day?.isoDate) return
                                            setWorkPeriods(
                                              removeWorkPeriod(workPeriods, day.isoDate),
                                            )
                                          }}
                                          copyDisabled={
                                            !day?.data?.start?.date ||
                                            !day?.data?.end?.date ||
                                            isResourceDisabled(resource)
                                          }
                                          pasteDisabled={
                                            clipboard.isEmpty ||
                                            clipboard.isEqualTo([
                                              dateToTimeInputValue(day?.data?.start?.date),
                                              dateToTimeInputValue(day?.data?.end?.date),
                                            ]) ||
                                            isResourceDisabled(resource) ||
                                            !!day?.metadata?.isDisabled ||
                                            !isClipboardDay(clipboard.value)
                                          }
                                          removeDisabled={
                                            isResourceDisabled(resource) ||
                                            !!day?.metadata?.isDisabled
                                          }
                                          removeDataTest="remove-work-period-action"
                                        />
                                      }
                                    >
                                      <WorkPeriodEditor
                                        //@ts-expect-error
                                        isoDate={day?.isoDate}
                                        workPeriods={workPeriods}
                                        jobTitle={jobTitle}
                                        initialWorkPeriods={initialWorkPeriods}
                                        resource={resource}
                                        setWorkPeriods={setWorkPeriods}
                                        errors={errors}
                                      />
                                    </Day>
                                  )
                                })}
                              </Week>
                            )
                          }),
                        )}
                        {errors.workPeriods && (
                          <tr>
                            <td colSpan={8}>
                              <FormFieldErrorMessage
                                error={errors.workPeriods as string | string[]}
                              />
                            </td>
                          </tr>
                        )}
                      </WeeksList>
                    )}
                  </>
                )
              }}
            </UseCalendar>
          )}
        </Clipboard>
      </ClipboardProvider>
    </CalendarProvider>
  )
}

const Clipboard = <T,>({
  children,
}: {
  children: (clipboard: ClipboardType<T>) => ReactElement<T>
}): ReactElement<T> => {
  const clipboard = useClipboardContext<T>()

  return children(clipboard)
}

type ClipboardDay = [IsoTime, IsoTime]
type ClipboardWeek = [
  ClipboardDay | null,
  ClipboardDay | null,
  ClipboardDay | null,
  ClipboardDay | null,
  ClipboardDay | null,
  ClipboardDay | null,
  ClipboardDay | null,
]
type ClipboardValues = ClipboardDay | ClipboardWeek

const isClipboardDay = (clipboardValue?: ClipboardValues): clipboardValue is ClipboardDay => {
  if (clipboardValue && clipboardValue.length === 2) return true
  return false
}
